import { useState, useEffect, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { FaEye, FaFileInvoiceDollar, FaMapMarkerAlt, FaPhone, FaPrint, FaUser } from "react-icons/fa";

// Component Imports
import PageHeading from "../../components/PageHeading";
import DynamicSelect from "../../components/DynamicSelect";
import AppointmentEditModal from "../Appointment/AppoinmentList/AppointmentEditModal";
import ViewBillModal from "../Pharmacy/ViewBillModal";
import BillDetailsModal from "../Pathology/BillDetailsModal";
import OutdoorViewBillModal from "../HospitalOffice/OutdoorBill/OutdoorViewBillModal";
import AmbulanceCallForm from "../Ambulance/Call/AddCallAmbulance";
import EmergencyViewBillModal from "../Emergency/EmergencyBill/EmergencyViewBillModal";
import HospitalViewBillModal from "../HospitalOffice/HospitalBill/HospitalViewBillModal";
import NicuViewBillModal from "../Nicu/NicuBill/NicuViewBillModal";
import PatientStatementPrint from "../PatientStatementPrint";

// API Config
const API_BASE_URL = "https://api.muktihospital.com/api";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const PatientLedger = () => {

  // -- State --
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ totalBill: 0, totalPaid: 0, totalDue: 0 });
  const [isPrintingStatement, setIsPrintingStatement] = useState(false);
  
  // New State for Filtering
  const [filterType, setFilterType] = useState("All");

  // -- Modal State --
  const [modalState, setModalState] = useState({ 
    isOpen: false, 
    type: null, 
    billId: null, 
    billData: null 
  });

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Patient Ledger" },
  ];

  // -- 1. Fetch Patient List --
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/patient`, { headers: { "x-api-key": API_KEY } });
        const options = res.data.map((p) => ({
          value: p.id,
          label: `${p.name} (${p.phoneNumber})`,
          details: p 
        }));
        setPatients(options);
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchPatients();
  }, []);

  // -- 2. Handle Patient Selection --
  const handlePatientSelect = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedPatient(null);
      setLedgerData([]);
      setStats({ totalBill: 0, totalPaid: 0, totalDue: 0 });
      return;
    }

    setSelectedPatient(selectedOption.details);
    setLoading(true);
    setFilterType("All");

    try {
      const res = await axios.get(`${API_BASE_URL}/patient/${selectedOption.value}/ledger`, { 
        headers: { "x-api-key": API_KEY } 
      });
      setLedgerData(res.data.ledger); 
      setStats(res.data.summary);
    } catch (error) {
      console.error("Error fetching ledger:", error);
      toast.error("Failed to load patient history.");
      setLedgerData([]); 
    } finally {
      setLoading(false);
    }
  };
  
  const filterOptions = useMemo(() => {
    const types = new Set(ledgerData.map(item => item.type));
    const uniqueTypes = ["All", ...Array.from(types)];
    return uniqueTypes.map(type => ({ value: type, label: type }));
  }, [ledgerData]);

  const filteredLedgerData = useMemo(() => {
    if (filterType === "All") return ledgerData;
    return ledgerData.filter(item => item.type === filterType);
  }, [ledgerData, filterType]);


  const BILL_CONFIG = {
    "Pharmacy Bill":   { url: "/medicine-bill",         modalType: "PHARMACY" },
    "Pathology Bill":  { url: "/pathology-bill",        modalType: "PATHOLOGY" },
    "Outdoor Bill":    { url: "/outdoor-bill",          modalType: "OUTDOOR" },
    "Ambulance Bill":  { url: "/ambulance-call",        modalType: "AMBULANCE" },
    "Emergency Bill":  { url: "/emergency/emergency-bills", modalType: "EMERGENCY_VIEW" },
    "Hospital Bill":   { url: "/hospital/hospital-bills",   modalType: "HOSPITAL_BILL_VIEW" },
    "NICU Bill":       { url: "/nicu/nicu-bills",       modalType: "NICU_BILL_VIEW" },
    "Appointment":     { url: null,                     modalType: "APPOINTMENT" }
  };

  const handleViewInvoice = async (item) => {
    const config = BILL_CONFIG[item.type];
    if (!config) return;

    if (item.type === "Appointment") {
      setModalState({ isOpen: true, type: config.modalType, billId: item.id });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}${config.url}/${item.id}`, {
        headers: { "x-api-key": API_KEY }
      });
      const billData = res.data.bill || res.data;

      if (billData) {
        setModalState({ isOpen: true, type: config.modalType, billId: item.id, billData: billData });
      }
    } catch (error) {
      console.error(`Error fetching ${item.type}:`, error);
      toast.error(`Failed to load ${item.type} details.`);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => setModalState({ ...modalState, isOpen: false, billData: null });

  const refreshLedger = () => {
    if (selectedPatient) {
      handlePatientSelect({ value: selectedPatient.id, details: selectedPatient });
    }
  };

  const handleDeleteSuccess = () => {
    refreshLedger();
    closeModal();
  };

  const handlePrintStatement = () => {
    setIsPrintingStatement(true);
  
    setTimeout(() => {
        window.print();
        setIsPrintingStatement(false);
    }, 100);
  };

  const displayPatient = selectedPatient || {
    name: "No Patient Selected",
    id: "---",
    phoneNumber: "---",
    address: "---",
    age: "--",
    gender: "--",
  };

  return (
    <div className={`bg-gray-50 pb-12 ${isPrintingStatement ? 'printing-statement-mode' : ''}`}>
      <style>{`
        #print-section {
          display: none;
        }

        @media print {
          .printing-statement-mode body * {
            visibility: hidden;
          }

          .printing-statement-mode #screen-section {
            display: none !important;
          }

          .printing-statement-mode #print-section {
            display: block !important;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            z-index: 9999;
            background: white;
            visibility: visible;
          }

          .printing-statement-mode #print-section * {
            visibility: visible;
          }

          .printing-statement-mode html, 
          .printing-statement-mode body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <ToastContainer />
      <PageHeading title="Patient Ledger & History" breadcrumbs={breadcrumbs} />

      <div>
        {/* --- SECTION 1: SEARCH BAR --- */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/60 p-1 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${selectedPatient ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
              <FaUser />
            </div>
            <div className="flex-1 w-full">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block ml-1">
                Find Patient
              </label>
              <div className="w-full">
                <DynamicSelect
                  options={patients}
                  onChange={handlePatientSelect}
                  placeholder="Search by Patient Name, ID or Mobile Number..."
                  isSearchable
                  isClearable
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="animate-fade-in-up">
          
          {/* --- SECTION 2: PATIENT PROFILE & FINANCIAL SUMMARY --- */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            
            {/* Profile Card (Span 4 cols) */}
            <div className="lg:col-span-4 flex flex-col">
              <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 h-full relative overflow-hidden transition-all duration-300 ${!selectedPatient ? 'opacity-70 grayscale' : ''}`}>
                
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 relative">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-md ring-4 ring-white ${selectedPatient ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                    {selectedPatient ? selectedPatient.name.charAt(0) : <FaUser size={24}/>}
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold leading-tight ${selectedPatient ? 'text-gray-800' : 'text-gray-400'}`}>
                      {displayPatient.name}
                    </h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                      {displayPatient.id}
                    </span>
                  </div>
                    </div>
                {/* Background Decoration */}
                <div className="opacity-20">
                  <FaUser size={60} />
                </div>
                </div>

                <div className="space-y-3 relative mt-5">
                  <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <FaPhone className={`${selectedPatient ? 'text-blue-500' : 'text-gray-400'}`} />
                    <span className="font-medium">{displayPatient.phoneNumber}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <FaMapMarkerAlt className={`${selectedPatient ? 'text-red-500' : 'text-gray-400'}`} />
                    <span className="truncate">{displayPatient.address || "No Address Provided"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-100">
                      <span className="block text-xs text-gray-500 uppercase">Age</span>
                      <span className="font-bold text-gray-800">{displayPatient.age} Y</span>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-100">
                      <span className="block text-xs text-gray-500 uppercase">Gender</span>
                      <span className="font-bold text-gray-800">{displayPatient.gender}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Stats (Span 8 cols) */}
            <div className="lg:col-span-8 flex flex-col justify-between gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 h-full">
                
                {/* Total Bill */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between relative overflow-hidden group hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Billed</p>
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-800">৳ {stats.totalBill.toLocaleString()}</h3>
                  </div>
                  <div>
                    <FaFileInvoiceDollar size={60} className="text-blue-600"/>
                  </div>
                  </div>
                  <div className="w-full h-1 bg-gray-100 mt-4 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 w-full"></div>
                  </div>
                </div>

                {/* Total Paid */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between relative overflow-hidden group hover:border-green-300 transition-colors">
                  <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Received</p>
                    <h3 className="text-3xl font-bold text-green-600">৳ {stats.totalPaid.toLocaleString()}</h3>
                  </div>
                  <div>
                    <FaFileInvoiceDollar size={60} className="text-green-600"/>
                  </div>
                  </div>
                  <div className="w-full h-1 bg-gray-100 mt-4 rounded-full overflow-hidden">
                    {/* Calculate percentage for bar width */}
                    <div className="h-full bg-green-500" style={{ width: stats.totalBill > 0 ? `${(stats.totalPaid / stats.totalBill) * 100}%` : '0%' }}></div>
                  </div>
                </div>

                {/* Total Due */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between relative overflow-hidden group hover:border-red-300 transition-colors">
                  <div className="flex items-center justify-between">
                      <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Due</p>
                    <h3 className="text-3xl font-bold text-red-500">৳ {stats.totalDue.toLocaleString()}</h3>
                  </div>
                  <div>
                    <FaFileInvoiceDollar size={60} className="text-red-600"/>
                  </div>
                  </div>
                  <div className="w-full h-1 bg-gray-100 mt-4 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500" style={{ width: stats.totalBill > 0 ? `${(stats.totalDue / stats.totalBill) * 100}%` : '0%' }}></div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* --- SECTION 3: TRANSACTION TABLE --- */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center bg-gray-50/50 gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-6 bg-M-primary-color rounded-full"></span>
                  Transaction History
                </h3>
                <p className="text-sm text-gray-500 mt-1 pl-4">
                  {selectedPatient ? "List of all medical bills and payments" : "Select a patient to view records"}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {selectedPatient && ledgerData.length > 0 && (
                  <div className="w-48"> 
                    <DynamicSelect 
                      options={filterOptions}
                      value={filterOptions.find(opt => opt.value === filterType) || { value: 'All', label: 'All' }}
                      onChange={(opt) => setFilterType(opt ? opt.value : 'All')}
                      placeholder="Filter by Type"
                      isSearchable={false}
                      className="w-full"
                    />
                  </div>
                )}

                <button 
                  onClick={handlePrintStatement}
                  disabled={!selectedPatient || ledgerData.length === 0}
                  className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:text-black transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPrint /> Print Statement
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1024px] text-sm text-left">
                <thead className="bg-gray-100/70 text-gray-600 uppercase font-semibold text-xs border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Invoice No</th>
                    <th className="px-6 py-4">Bill Type</th>
                    <th className="px-6 py-4 text-right">Amount</th>
                    <th className="px-6 py-4 text-right">Paid</th>
                    <th className="px-6 py-4 text-right">Due</th>
                    <th className="px-6 py-4 text-center">Status</th>
                    <th className="px-6 py-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-20 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                        <p className="mt-2 text-gray-500 text-sm">Loading ledger data...</p>
                      </td>
                    </tr>
                  ) : filteredLedgerData.length > 0 ? (
                    filteredLedgerData.map((item, index) => (
                      <tr key={index} className="hover:bg-blue-50/30 transition duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-medium">
                          {new Date(item.date).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                            {item.billNo || item.id}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                            ${item.type === 'Emergency Bill' ? 'bg-red-50 text-red-700 border-red-100' : 
                              item.type === 'Hospital Bill' ? 'bg-purple-50 text-purple-700 border-purple-100' : 
                              'bg-blue-50 text-blue-700 border-blue-100'}`}>
                            {item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-gray-800">৳{item.amount.toLocaleString()}</td>
                        <td className="px-6 py-4 text-right font-medium text-green-600">৳{item.paid.toLocaleString()}</td>
                        <td className="px-6 py-4 text-right font-bold text-red-500">
                          {item.amount - item.paid > 0 ? `৳${(item.amount - item.paid).toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {item.amount - item.paid <= 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span> Due
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button 
                            onClick={() => handleViewInvoice(item)}
                            className="size-8 rounded-full bg-white text-M-primary-color border border-gray-200 flex items-center justify-center hover:bg-M-primary-color hover:text-white hover:border-M-primary-color transition-all duration-300 shadow-sm mx-auto"
                            title="View Details"
                          >
                            <FaEye size={14} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-300">
                          <FaFileInvoiceDollar size={48} className="mb-4" />
                          <p className="text-base font-medium text-gray-500">
                            {selectedPatient ? "No transaction history found." : "Please select a patient to view data."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div id="print-section">
        {selectedPatient && (
          <PatientStatementPrint 
            patient={selectedPatient}
            transactions={filteredLedgerData}
            filterType={filterType}
          />
        )}
      </div>

      {/* --- MODALS SECTION --- */}
      
      {/* 1. Emergency View */}
      {modalState.type === 'EMERGENCY_VIEW' && (
        <EmergencyViewBillModal
          isOpen={modalState.isOpen}
          bill={modalState.billData}
          onClose={closeModal}
          onEdit={refreshLedger}
          onDelete={handleDeleteSuccess}
        />
      )}

      {/* 2. Hospital View */}
      {modalState.type === 'HOSPITAL_BILL_VIEW' && (
        <HospitalViewBillModal
          isOpen={modalState.isOpen}
          bill={modalState.billData}
          onClose={closeModal}
          onEdit={refreshLedger}
          onDelete={handleDeleteSuccess}
        />
      )}

      {/* 3. NICU View */}
      {modalState.type === 'NICU_BILL_VIEW' && (
        <NicuViewBillModal
          isOpen={modalState.isOpen}
          bill={modalState.billData}
          onClose={closeModal}
          onEdit={refreshLedger}
          onDelete={handleDeleteSuccess}
        />
      )}

      {/* 4. Appointment Edit */}
      {modalState.type === 'APPOINTMENT' && (
        <AppointmentEditModal
          isOpen={modalState.isOpen}
          appointmentId={modalState.billId}
          onClose={closeModal}
          onSaveSuccess={refreshLedger}
        />
      )}

      {/* 5. Pharmacy View */}
      {modalState.type === 'PHARMACY' && (
        <ViewBillModal
          isOpen={modalState.isOpen}
          billId={modalState.billId}
          onClose={closeModal}
          bill={modalState.billData}
          onEdit={() => {}}
        />
      )}

      {/* 6. Pathology View */}
      {modalState.type === 'PATHOLOGY' && (
        <BillDetailsModal
          isOpen={modalState.isOpen}
          billDetails={modalState.billData} 
          onClose={closeModal}
          onDelete={() => {}} 
          onSave={() => {}}
        />
      )}

      {/* 7. Outdoor View */}
      {modalState.type === 'OUTDOOR' && (
        <OutdoorViewBillModal
          isOpen={modalState.isOpen}
          bill={modalState.billData}
          onClose={closeModal}
          onDelete={() => {}} 
          onSave={() => {}}
        />
      )}

      {/* 8. Ambulance Edit/View */}
      {modalState.type === 'AMBULANCE' && (
        <AmbulanceCallForm
          isOpen={modalState.isOpen}
          onClose={closeModal}
          callId={modalState.billId}
          initialData={modalState.billData}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};

export default PatientLedger;